import React, { useEffect, useState } from 'react';
import { AuthorizationsService } from '../../../../services/patientmr/authorizations.service';
import type { PAMcoOption } from '../../../../dtos/patientmr/authorizations.dto';

interface McoPickerProps {
  /** Patient whose insurance settings carry systemConfig.payers.medicaidMcos */
  patientId?: string;
  program: string;
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
}

/**
 * MCO select fed by systemConfig.payers.medicaidMcos (Phase 8 §3.1, R-180).
 * The PA stores the MCO label in payerName. A saved value that is not in the list (a legacy
 * name or code) is still shown as its own option so existing PAs keep displaying it.
 */
export const McoPicker: React.FC<McoPickerProps> = ({ patientId, program, value, onChange, disabled = false }) => {
  const [isOther, setIsOther] = useState(false);
  const [mcos, setMcos] = useState<PAMcoOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(false);

  useEffect(() => {
    if (!patientId) return undefined;
    let cancelled = false;
    setLoading(true);
    setLoadError(false);
    AuthorizationsService.getMedicaidMcos(patientId)
      .then(list => { if (!cancelled) setMcos(list); })
      .catch(() => { if (!cancelled) setLoadError(true); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [patientId]);

  const list = AuthorizationsService.filterMcosForProgram(mcos, program)
    .filter(m => !m.legacy || m.label === value || m.code === value);
  const known = list.some(m => m.label === value);
  const legacyMatch = !known && value ? mcos.find(m => m.code === value) : undefined;

  if (isOther) {
    return (
      <div className="space-y-1.5">
        <input
          type="text"
          value={value}
          onChange={e => onChange(e.target.value)}
          placeholder="Enter MCO name..."
          className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-slate-500 focus:border-transparent transition-colors"
          disabled={disabled}
          autoFocus
        />
        <button
          onClick={() => { setIsOther(false); onChange(''); }}
          className="text-xs text-blue-600 hover:text-blue-700"
          type="button"
          disabled={disabled}
        >
          &larr; Back to list
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-1">
      <select
        value={value}
        onChange={e => {
          if (e.target.value === '__other__') { setIsOther(true); onChange(''); }
          else onChange(e.target.value);
        }}
        className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-slate-500 focus:border-transparent transition-colors appearance-none bg-white"
        disabled={disabled}
        aria-label="MCO"
      >
        <option value="">{loading ? 'Loading...' : 'Select...'}</option>
        {list.map(m => <option key={m.code} value={m.label}>{m.label}</option>)}
        {value && !known && (
          <option value={value}>{legacyMatch ? legacyMatch.label : value}</option>
        )}
        <option value="__other__">Other (type manually)</option>
      </select>
      {loadError && (
        <p className="text-xs text-amber-700 text-left">MCO list unavailable. Use Other to type the name.</p>
      )}
    </div>
  );
};

export default McoPicker;
